import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { PaymentMethod, PosSession, Product } from '../../shared/api/types';
import { Alert, Button, Card, Field } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney, parseMoney } from '../finance/money';
import { PAYMENT } from './labels';

const money = (c: number) => formatMoney(c, 'MXN');

/** Punto de venta de un turno abierto: armar el ticket, cobrar, gastos y retiros, cerrar con conteo. */
export function PosPanel({ churchId, session, products, onChange }: { churchId: string; session: PosSession; products: Product[]; onChange: (m: string) => void }) {
  const [ticket, setTicket] = useState<Map<string, number>>(new Map());
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [customer, setCustomer] = useState('');
  const [walletPerson, setWalletPerson] = useState('');
  const wallets = useAsync(
    (signal) => (method === 'wallet' ? api.get<{ items: { personId: string; name: string; balanceCents: number }[] }>(`/commerce/churches/${churchId}/wallets`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId, method],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = products.filter((p) => p.active);
  const lines = [...ticket].map(([id, qty]) => ({ product: products.find((p) => p.id === id)!, qty })).filter((l) => l.product);
  const total = lines.reduce((t, l) => t + l.product.priceCents * l.qty, 0);
  const change = (id: string, by: number) =>
    setTicket((t) => {
      const next = new Map(t);
      const qty = (next.get(id) ?? 0) + by;
      if (qty <= 0) next.delete(id);
      else next.set(id, Math.min(qty, 999));
      return next;
    });

  async function charge() {
    setBusy(true);
    setError(null);
    try {
      const order = await api.post<{ number: number }>(`/commerce/churches/${churchId}/sessions/${session.id}/orders`, {
        items: lines.map((l) => ({ productId: l.product.id, quantity: l.qty })),
        paymentMethod: method,
        customerName: customer.trim() || (method === 'wallet' && wallets.status === 'ready' ? wallets.data.find((w) => w.personId === walletPerson)?.name ?? null : null),
        customerPersonId: method === 'wallet' ? walletPerson || null : null,
      });
      setTicket(new Map());
      setCustomer('');
      setWalletPerson('');
      onChange(`Pedido ${order.number} cobrado.`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack-grid">
      <Card title={`Vender · ${session.register.name}`}>
        <div className="pos__products">
          {active.map((p) => (
            <button key={p.id} type="button" className="pos__product" onClick={() => change(p.id, 1)}>
              <strong>{p.name}</strong>
              <span>{money(p.priceCents)}</span>
            </button>
          ))}
        </div>
        {active.length === 0 ? <p className="muted small">No hay productos activos.</p> : null}
      </Card>
      <Card title="Ticket">
        {lines.length === 0 ? <p className="muted small">Toca un producto para agregarlo.</p> : null}
        <ul className="list">
          {lines.map((l) => (
            <li key={l.product.id} className="list__item pos__line">
              <span>{l.product.name}</span>
              <span className="pos__qty">
                <Button variant="ghost" onClick={() => change(l.product.id, -1)} aria-label={`Quitar un ${l.product.name}`}>
                  −
                </Button>
                <strong aria-label={`Cantidad de ${l.product.name}`}>{l.qty}</strong>
                <Button variant="ghost" onClick={() => change(l.product.id, 1)} aria-label={`Agregar un ${l.product.name}`}>
                  +
                </Button>
              </span>
              <span className="money">{money(l.product.priceCents * l.qty)}</span>
            </li>
          ))}
        </ul>
        <p className="pos__total">
          Total <strong className="money">{money(total)}</strong>
        </p>
        <fieldset className="choices choices--row">
          <legend>Forma de pago</legend>
          {(Object.keys(PAYMENT) as PaymentMethod[]).map((m) => (
            <label key={m} className="choice">
              <input type="radio" name="payment" checked={method === m} onChange={() => setMethod(m)} />
              <span>{PAYMENT[m]}</span>
            </label>
          ))}
        </fieldset>
        {method === 'wallet' ? (
          <div className="field">
            <label htmlFor="pos-wallet">¿De quién es el monedero?</label>
            <select id="pos-wallet" value={walletPerson} onChange={(e) => setWalletPerson(e.target.value)}>
              <option value="">Elige a la persona…</option>
              {(wallets.status === 'ready' ? wallets.data : []).map((w) => (
                <option key={w.personId} value={w.personId} disabled={w.balanceCents < total}>
                  {w.name} · saldo {money(w.balanceCents)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <Field label="Nombre para el pedido (opcional)" value={customer} onChange={(e) => setCustomer(e.target.value)} maxLength={80} />
        {error ? <Alert>{error}</Alert> : null}
        <div className="actions">
          <Button busy={busy} disabled={lines.length === 0 || (method === 'wallet' && !walletPerson)} onClick={charge}>
            Cobrar {money(total)}
          </Button>
        </div>
      </Card>
      <SessionCard churchId={churchId} session={session} onChange={onChange} />
    </div>
  );
}

function SessionCard({ churchId, session: s, onChange }: { churchId: string; session: PosSession; onChange: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [panel, setPanel] = useState<'cash' | 'close' | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const amount = parseMoney(String(form.get('amount') ?? ''));
    const counted = String(form.get('amount') ?? '').trim() === '0' ? 0 : amount;
    if (counted === null) {
      setError('Escribe un monto válido.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (panel === 'cash') {
        await api.post(`/commerce/churches/${churchId}/sessions/${s.id}/cash`, { kind: String(form.get('kind')), amountCents: counted, note: String(form.get('note')) });
        onChange('Movimiento de caja registrado.');
      } else {
        const closed = await api.post<PosSession>(`/commerce/churches/${churchId}/sessions/${s.id}/close`, { countedCashCents: counted, note: String(form.get('note') || '') || null });
        const diff = closed.differenceCents ?? 0;
        onChange(`Turno cerrado. ${diff === 0 ? 'La caja cuadra.' : diff > 0 ? `Sobran ${money(diff)}.` : `Faltan ${money(-diff)}.`}`);
      }
      setPanel(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const t = s.totals;
  return (
    <Card title="Turno">
      <dl className="answers">
        <div>
          <dt>Abrió</dt>
          <dd>
            {s.openedBy.name}, {new Date(s.openedAt).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })} · fondo {money(t.openingFloatCents)}
          </dd>
        </div>
        <div>
          <dt>Ventas ({t.orders} pedidos)</dt>
          <dd>
            {(Object.keys(PAYMENT) as PaymentMethod[]).map((m) => `${PAYMENT[m]} ${money(t.sales[m] - t.refunds[m])}`).join(' · ')}
          </dd>
        </div>
        <div>
          <dt>Gastos y retiros</dt>
          <dd>
            {money(t.expensesCents)} · {money(t.withdrawalsCents)}
          </dd>
        </div>
        <div>
          <dt>Efectivo esperado en caja</dt>
          <dd>
            <strong className="money">{money(s.expectedCashCents)}</strong>
          </dd>
        </div>
      </dl>
      {panel === null ? (
        <div className="actions">
          <Button variant="secondary" onClick={() => setPanel('cash')}>
            Gasto o retiro
          </Button>
          <Button variant="ghost" onClick={() => setPanel('close')}>
            Cerrar turno
          </Button>
        </div>
      ) : (
        <form className="form" onSubmit={submit} noValidate>
          {panel === 'cash' ? (
            <div className="field">
              <label htmlFor="cash-kind">Tipo</label>
              <select id="cash-kind" name="kind" defaultValue="expense">
                <option value="expense">Gasto pagado con la caja</option>
                <option value="withdrawal">Retiro de efectivo</option>
              </select>
            </div>
          ) : null}
          <Field label={panel === 'close' ? 'Efectivo contado' : 'Monto'} name="amount" inputMode="decimal" required autoComplete="off" />
          <Field label={panel === 'close' ? 'Nota (opcional)' : 'Concepto'} name="note" maxLength={300} required={panel === 'cash'} />
          {error ? <Alert>{error}</Alert> : null}
          <div className="actions">
            <Button type="submit" busy={busy} variant={panel === 'close' ? 'danger' : 'primary'}>
              {panel === 'close' ? 'Cerrar turno' : 'Registrar'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setPanel(null)}>
              Cancelar
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
